import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";
import { ChevronLeft, FilePlus2, Plus, Trash2, Loader2, Building2, Truck, Package } from "lucide-react";
import ConductorSelect from "@/components/ConductorSelect";
import MatriculaSelect from "@/components/MatriculaSelect";
import { esCpIntl, esDireccionCompleta, esEmail, esIdFiscal, esNif } from "@/lib/validacionEs";
import { irAGenerados, postGenerarConRecuperacion } from "@/lib/generarDeca";
import AvisoSalida, { useAvisoSalida } from "@/components/AvisoSalida";
import ConfirmarBorrador from "@/components/ConfirmarBorrador";
import { LEN } from "@/lib/limites";
import DestinoClienteSelect, { fmtDomicilio } from "@/components/DestinoClienteSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const T = {};
T.historico = "Hist" + String.fromCharCode(0xf3) + "rico";
T.matriculas = "Matr" + String.fromCharCode(0xed) + "culas (coma)";
T.autorizacion = "Autorizaci" + String.fromCharCode(0xf3) + "n especial";
T.anadirAlbaran = "A" + String.fromCharCode(0xf1) + "adir albar" + String.fromCharCode(0xe1) + "n";
T.albaranN = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n; };
T.direccion = "Destino (direcci" + String.fromCharCode(0xf3) + "n)";
T.naturaleza = "Naturaleza de la mercanc" + String.fromCharCode(0xed) + "a";
T.nAlbaran = "N" + String.fromCharCode(0xba) + " albar" + String.fromCharCode(0xe1) + "n";
T.razonSocial = "Raz" + String.fromCharCode(0xf3) + "n social *";
T.codigoPostal = "C" + String.fromCharCode(0xf3) + "digo postal";
T.catalogo = "No se pudo cargar el cat" + String.fromCharCode(0xe1) + "logo";
T.razonNif = "Raz" + String.fromCharCode(0xf3) + "n social y NIF son obligatorios";
T.guardado = "Guardado en el cat" + String.fromCharCode(0xe1) + "logo";
T.anadeAlbaran = "A" + String.fromCharCode(0xf1) + "ade al menos un albar" + String.fromCharCode(0xe1) + "n";
T.faltaDestino = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": falta el cliente destino"; };
T.faltaDireccion = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": falta la direcci" + String.fromCharCode(0xf3) + "n de destino"; };
T.faltaNumero = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": falta el n" + String.fromCharCode(0xfa) + "mero"; };
T.faltaNaturaleza = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": falta la naturaleza de la mercanc" + String.fromCharCode(0xed) + "a"; };
T.faltaPeso = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": el peso bruto debe ser mayor que 0"; };
T.faltaBultos = function (n) { return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": los bultos deben ser mayor que 0"; };
T.faltaVehiculo = "Indica la matr" + String.fromCharCode(0xed) + "cula del veh" + String.fromCharCode(0xed) + "culo";
T.nifInvalido = "NIF / CIF / NIE no v" + String.fromCharCode(0xe1) + "lido (letra de control)";
T.nifExtranjero = "Identificador fiscal no v" + String.fromCharCode(0xe1) + "lido. NIF/CIF/NIE espa" + String.fromCharCode(0xf1) + "ol (letra de control) o VAT extranjero (p. ej. PT123456789).";
T.emailInvalido = "Correo no v" + String.fromCharCode(0xe1) + "lido";
T.correoEnvio = "Correo de env" + String.fromCharCode(0xed) + "o";
T.cpInvalido = "C" + String.fromCharCode(0xf3) + "digo postal no v" + String.fromCharCode(0xe1) + "lido (5 d" + String.fromCharCode(0xed) + "gitos, provincia 01-52)";
T.mercanciaGeneral = "Mercanc" + String.fromCharCode(0xed) + "a general";
T.copiaAlbaran = "Calle, CP, municipio y provincia. Se copia a cada albar" + String.fromCharCode(0xe1) + "n; puedes ajustarlo env" + String.fromCharCode(0xed) + "o a env" + String.fromCharCode(0xed) + "o.";
T.origenIncompleto = function (n) {
  return "Albar" + String.fromCharCode(0xe1) + "n " + n + ": el origen necesita calle, c" + String.fromCharCode(0xf3) + "digo postal, municipio y provincia";
};
T.origenEmpresaIncompleto = "El origen del cargador necesita calle, c" + String.fromCharCode(0xf3) + "digo postal, municipio y provincia";
T.origenPh = "Calle, CP, municipio y provincia";
T.sesionConductor = "Con sesi" + String.fromCharCode(0xf3) + "n de conductor no se elige transportista.";
T.envios = "Albaranes / env" + String.fromCharCode(0xed) + "os";
T.mismosDatos = "Los mismos datos que salen en la tabla del PDF: origen, destinatario, naturaleza, bultos, peso y albar" + String.fromCharCode(0xe1) + "n.";
T.vacio = "vac" + String.fromCharCode(0xed) + "o si no hay";
T.buscarCargador = "Buscar cargador o NIF" + String.fromCharCode(0x2026);
T.buscarTransp = "Buscar transportista o NIF" + String.fromCharCode(0x2026);
T.buscarCliente = "Buscar cliente destino" + String.fromCharCode(0x2026);
T.buscarProveedor = "Buscar proveedor origen" + String.fromCharCode(0x2026);
T.faltaProveedor = "Selecciona el proveedor de origen";
T.tipoCliente = "A cliente";
T.tipoProveedor = "A proveedor";
T.origenProveedor = "Origen (domicilio del proveedor)";
T.destinoEmpresa = "Destino (cargador de la empresa)";
T.hintProveedor = "El origen es el proveedor. El destino es el cargador de la empresa.";
T.dash = String.fromCharCode(0x2014);
T.dot = " " + String.fromCharCode(0xb7) + " ";
T.subtitle = "Para clientes sin Sage. Elige si todos los albaranes van en un DeCA o se agrupan por destino.";
T.reglaUnico = "Todos los albaranes de este formulario salen en un solo DeCA.";
T.reglaGrupos = "Los albaranes del mismo cliente / destino se agrupan. Cada destino distinto genera su DeCA.";
T.seGeneraran = function (n) {
  return n === 1 ? "Se generar" + String.fromCharCode(0xe1) + " 1 DeCA" : "Se generar" + String.fromCharCode(0xe1) + "n " + n + " DeCA";
};

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function emptyParte() {
  return { razon_social: "", nif: "", domicilio: "", municipio: "", cp: "", provincia: "", email: "" };
}

function parteKey(p) {
  const nif = (p?.nif || "").toUpperCase();
  if (nif) return "nif:" + nif;
  const cod = p?.codigo != null && p?.codigo !== "" ? String(p.codigo) : "";
  if (cod) return "cod:" + cod;
  return "nom:" + (p?.razon_social || "").toLowerCase();
}

function txtDom(s) {
  return String(s || "").toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ").trim();
}

function contieneDom(hay, needle) {
  const n = txtDom(needle);
  return !!n && txtDom(hay).includes(n);
}

function colapsarDomicilio(s) {
  const out = [];
  for (const p of String(s || "").split(",").map((x) => x.trim()).filter(Boolean)) {
    if (contieneDom(out.join(", "), p)) continue;
    out.push(p);
  }
  return out.join(", ");
}

function destinoDe(p) {
  const calle = colapsarDomicilio(p?.domicilio);
  const cp = (p?.cp || "").trim();
  const muni = (p?.municipio || "").trim();
  const prov = (p?.provincia || "").trim();
  let joined = calle;
  const bloque = [cp, muni].filter(Boolean).join(" ");
  if (bloque && !contieneDom(joined, bloque) && !(cp && contieneDom(joined, cp) && (!muni || contieneDom(joined, muni)))) {
    joined = [joined, bloque].filter(Boolean).join(", ");
  }
  if (prov && !contieneDom(joined, prov)) {
    joined = [joined, prov].filter(Boolean).join(", ");
  }
  return joined;
}

function claveClienteEnvio(e, idx) {
  const nif = String(e?.destinatario?.nif || "").trim().toUpperCase();
  const nom = String(e?.destinatario?.razon_social || "").trim().toLowerCase();
  if (nif) return "nif:" + nif;
  if (nom) return "nom:" + nom;
  return "row:" + (e?.key || idx);
}

function bultosDefectoDe(src) {
  if (!src || !src.bultos_defecto_activo) return "";
  const n = Number(src.bultos_defecto);
  return n > 0 ? String(n) : "";
}

function emptyEnvio(origen, naturaleza, bultos) {
  return {
    key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    origen: origen || "",
    destinatario: emptyParte(),
    destino: "",
    dest_id: "",
    orig_id: "cargador",
    naturaleza: naturaleza || T.mercanciaGeneral,
    bultos: bultos || "",
    peso_bruto: "",
    albaran_ejercicio: String(new Date().getFullYear()),
    albaran_serie: "",
    albaran_numero: "",
  };
}

function coincideParte(p, s) {
  return `${p.razon_social || ""} ${p.nif || ""} ${p.municipio || ""} ${p.codigo || ""}`.toLowerCase().includes(s);
}

function tieneDomicilioParte(p) {
  const calle = (p?.domicilio || "").trim();
  const cp = (p?.cp || "").trim();
  const muni = (p?.municipio || "").trim();
  return Boolean(calle || (cp && muni));
}

function aptoPickerDeca(p, tipo) {
  if (!p || !(p.razon_social || "").trim()) return false;
  if (tipo === "cliente" || tipo === "proveedor") {
    return esIdFiscal(p.nif) && tieneDomicilioParte(p);
  }
  return true;
}

function PartePicker({ label, items, value, onChange, onCreate, placeholder, tipo }) {
  const fid = React.useId();
  const boxRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cerrarSiFuera = (e) => {
      const el = boxRef.current;
      if (!el) return;
      const t = e.target;
      if (t && el.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", cerrarSiFuera, true);
    document.addEventListener("focusin", cerrarSiFuera, true);
    return () => {
      document.removeEventListener("pointerdown", cerrarSiFuera, true);
      document.removeEventListener("focusin", cerrarSiFuera, true);
    };
  }, [open]);

  useEffect(() => {
    const s = q.trim();
    if (!tipo || s.length < 2) {
      setRemote([]);
      setBuscando(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get("/api/v1/partes", {
          params: { tipo, page: 1, page_size: 40, buscar: s },
        });
        if (!cancelled) setRemote(data.items || []);
      } catch {
        if (!cancelled) setRemote([]);
      } finally {
        if (!cancelled) setBuscando(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, tipo]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const local = (items || []).filter((p) => aptoPickerDeca(p, tipo));
    const loc = (!s ? local : local.filter((p) => coincideParte(p, s))).slice(0, 12);
    if (!s || remote.length === 0) return loc;
    const seen = new Set(loc.map(parteKey));
    const extra = [];
    for (const p of remote) {
      if (!aptoPickerDeca(p, tipo)) continue;
      const k = parteKey(p);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      extra.push(p);
    }
    return [...loc, ...extra].slice(0, 24);
  }, [items, q, remote, tipo]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 min-h-7">
        <Label htmlFor={fid} className="label-tag">{label}</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onCreate}>
          <Plus className="h-3 w-3 mr-1"/> Nuevo
        </Button>
      </div>
      {value?.razon_social ? (
        <div className="mt-1 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
          <div className="text-sm font-semibold text-slate-900">{value.razon_social}</div>
          <div className="text-xs text-slate-500 font-mono">NIF {value.nif || T.dash}</div>
          {destinoDe(value) ? <div className="text-xs text-slate-500 mt-0.5">{destinoDe(value)}</div> : null}
          <Button type="button" variant="ghost" size="sm" className="h-7 px-0 mt-1 text-xs" onClick={() => { onChange(null); setQ(""); setOpen(true); }}>Cambiar</Button>
        </div>
      ) : (
        <div className="relative mt-1" ref={boxRef}>
          <Input
            id={fid}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
          />
          {open ? (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white border border-slate-200 rounded-md shadow-sm">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">
                  {buscando ? "Buscando en Sage" + String.fromCharCode(0x2026) : "Sin coincidencias. Crea uno nuevo."}
                </div>
              ) : filtered.map((p) => (
                <button
                  type="button"
                  key={parteKey(p)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  onClick={() => { onChange(p); setQ(""); setOpen(false); }}
                >
                  <div className="text-sm font-medium text-slate-900">{p.razon_social}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {p.nif || (p.codigo != null && p.codigo !== "" ? "C" + String.fromCharCode(0xf3) + "d. " + p.codigo : "")}
                    {p.municipio ? `${T.dot}${p.municipio}` : ""}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function ManualDecaPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const esConductor = user?.rol === "conductor";
  const [opciones, setOpciones] = useState(null);
  const [cargador, setCargador] = useState(null);
  const [transportista, setTransportista] = useState(null);
  const [fecha, setFecha] = useState(hoyISO());
  const [fechaDescarga, setFechaDescarga] = useState("");
  const [vehiculo, setVehiculo] = useState("");
  const [remolque, setRemolque] = useState("");
  const [conductor, setConductor] = useState("");
  const [precio, setPrecio] = useState("");
  const [autorizacion, setAutorizacion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [origen, setOrigen] = useState("");
  const [envios, setEnvios] = useState([]);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTipo, setCreateTipo] = useState("cliente");
  const [createForm, setCreateForm] = useState(emptyParte());
  const [createTarget, setCreateTarget] = useState(null);
  const [askBorrador, setAskBorrador] = useState(false);
  const [modo, setModo] = useState(user?.modo_agrupacion === "agrupado" ? "agrupado" : "unico");
  const [tipoDeca, setTipoDeca] = useState("cliente");
  const [proveedor, setProveedor] = useState(null);
  const [cargadorContractual, setCargadorContractual] = useState("empresa");
  const puedeProveedor = !!user?.deca_proveedores;
  const puedeElegirCargador = !!(user?.cargador_es_cliente || opciones?.cargador_es_cliente) && tipoDeca !== "proveedor";

  const sucio = useMemo(() => {
    if (transportista?.nif || vehiculo || remolque || conductor || precio || autorizacion || observaciones || fechaDescarga) return true;
    if (envios.length > 1) return true;
    return envios.some((e) =>
      e.destinatario?.razon_social || e.destino || e.bultos || e.peso_bruto || e.albaran_numero || e.albaran_serie
    );
  }, [transportista, vehiculo, remolque, conductor, precio, autorizacion, observaciones, envios]);
  const gruposDestino = useMemo(() => {
    const map = new Map();
    envios.forEach((e, i) => {
      const k = claveClienteEnvio(e, i);
      if (!map.has(k)) {
        map.set(k, { n: 0, nombre: e.destinatario?.razon_social || T.albaranN(i + 1) });
      }
      const b = map.get(k);
      b.n += 1;
      if (e.destinatario?.razon_social) b.nombre = e.destinatario.razon_social;
    });
    return [...map.values()];
  }, [envios]);
  const nDecas = !envios.length ? 0 : (modo === "agrupado" ? Math.max(gruposDestino.length, 1) : 1);
  const { blocker, saliendo, liberar, confirmar, cancelar } = useAvisoSalida(sucio);

  const load = async () => {
    try {
      const { data } = await api.get("/api/v1/decas/manual/opciones");
      setOpciones(data);
      const defCar = data.cargador_default && (data.cargador_default.nif || data.cargador_default.razon_social)
        ? data.cargador_default
        : null;
      setCargador((prev) => prev || defCar);
      setOrigen((prev) => prev || colapsarDomicilio(data.origen_default || ""));
      if (data.modo_agrupacion === "agrupado" || data.modo_agrupacion === "unico") {
        setModo(data.modo_agrupacion);
      }
      setEnvios((prev) => prev.length ? prev : [emptyEnvio(data.origen_default || "", data.naturaleza_por_defecto || user?.naturaleza_por_defecto, bultosDefectoDe(data) || bultosDefectoDe(user))]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || T.catalogo);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = (tipo, target, preset) => {
    setCreateTipo(tipo);
    setCreateTarget(target);
    setCreateForm({ ...emptyParte(), ...(preset || {}) });
    setCreateOpen(true);
  };

  const saveParte = async () => {
    if (!createForm.razon_social.trim() || !createForm.nif.trim()) {
      toast.error(T.razonNif);
      return;
    }
    if (createTipo === "cliente" || createTipo === "proveedor" || createTipo === "transportista") {
      if (!esIdFiscal(createForm.nif)) {
        toast.error(T.nifExtranjero);
        return;
      }
    } else if (!esNif(createForm.nif)) {
      toast.error(T.nifInvalido);
      return;
    }
    if (createForm.cp.trim() && !esCpIntl(createForm.cp)) {
      toast.error(T.cpInvalido);
      return;
    }
    if (createForm.email.trim() && !esEmail(createForm.email)) {
      toast.error(T.emailInvalido);
      return;
    }
    try {
      const { data } = await api.post("/api/v1/partes", { tipo: createTipo, ...createForm });
      setCreateOpen(false);
      if (createTarget === "cargador") setCargador(data);
      else if (createTarget === "transportista") setTransportista(data);
      else if (createTarget === "proveedor") {
        setProveedor(data);
        const ori = destinoDe(data);
        setOrigen(ori);
        setEnvios((rows) => rows.map((r) => ({ ...r, origen: ori || r.origen })));
      }
      else if (typeof createTarget === "number") {
        const dest = destinoDe(data) || destinoDe(createForm);
        setEnvios((rows) => rows.map((r, i) => i === createTarget ? {
          ...r,
          destinatario: { ...data, domicilio: createForm.domicilio, municipio: createForm.municipio, cp: createForm.cp, provincia: createForm.provincia },
          cliente_dir: {
            domicilio: createForm.domicilio || data.domicilio || "",
            municipio: createForm.municipio || data.municipio || "",
            cp: createForm.cp || data.cp || "",
            provincia: createForm.provincia || data.provincia || "",
          },
          destino: dest,
          dest_id: dest ? "cliente" : "",
        } : r));
      }
      await load();
      toast.success(T.guardado);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setEnvio = (idx, patch) => {
    setEnvios((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const aplicarTipo = (tipo, prov = proveedor, car = cargador) => {
    setTipoDeca(tipo);
    if (tipo === "proveedor") {
      const dest = destinoDe(car);
      const ori = destinoDe(prov);
      if (ori) setOrigen(ori);
      setEnvios((rows) => rows.map((r) => ({
        ...r,
        destinatario: car || emptyParte(),
        cliente_dir: car ? {
          domicilio: car.domicilio || "",
          municipio: car.municipio || "",
          cp: car.cp || "",
          provincia: car.provincia || "",
        } : null,
        destino: dest,
        dest_id: dest ? "empresa" : "",
        origen: ori || r.origen,
      })));
      return;
    }
    const ori = destinoDe(car) || (opciones?.origen_default || "");
    setOrigen(ori);
    setEnvios((rows) => rows.map((r) => ({
      ...r,
      destinatario: emptyParte(),
      cliente_dir: null,
      destino: "",
      dest_id: "",
      origen: ori || r.origen,
      orig_id: "cargador",
    })));
  };

  const generar = async (comoBorrador = false) => {
    if (!cargador?.razon_social || !cargador?.nif) {
      toast.error("Selecciona o crea el cargador");
      return;
    }
    if (tipoDeca === "proveedor" && (!proveedor?.razon_social || !proveedor?.nif)) {
      toast.error(T.faltaProveedor);
      return;
    }
    if (!esConductor && !comoBorrador && (!transportista?.razon_social || !transportista?.nif)) {
      toast.error("Selecciona o crea el transportista");
      return;
    }
    if (!vehiculo.trim() && !comoBorrador) {
      setAskBorrador(true);
      return;
    }
    if (!fecha) {
      toast.error("Indica la fecha de transporte");
      return;
    }
    if (!envios.length) {
      toast.error(T.anadeAlbaran);
      return;
    }
    if (!esDireccionCompleta(origen) && envios.every((e) => !esDireccionCompleta(e.origen))) {
      toast.error(T.origenEmpresaIncompleto);
      return;
    }
    for (const [i, e] of envios.entries()) {
      if (!esDireccionCompleta(e.origen || origen)) {
        toast.error(T.origenIncompleto(i + 1));
        return;
      }
      if (!e.destinatario?.razon_social || !e.destinatario?.nif) {
        toast.error(T.faltaDestino(i + 1));
        return;
      }
      if (!(e.destino || "").trim()) {
        toast.error(T.faltaDireccion(i + 1));
        return;
      }
      if (!e.naturaleza?.trim()) {
        toast.error(T.faltaNaturaleza(i + 1));
        return;
      }
      if (e.destinatario?.cp && !esCpIntl(e.destinatario.cp)) {
        toast.error("Albar" + String.fromCharCode(0xe1) + "n " + (i + 1) + ": " + T.cpInvalido);
        return;
      }
      if (!(Number(String(e.peso_bruto).replace(",", ".")) > 0)) {
        toast.error(T.faltaPeso(i + 1));
        return;
      }
      if (!(Number(e.bultos) > 0)) {
        toast.error(T.faltaBultos(i + 1));
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        cargador,
        transportista: esConductor ? undefined : (transportista || undefined),
        fecha_transporte: fecha,
        fecha_descarga: fechaDescarga || null,
        matriculas: vehiculo.trim() ? [vehiculo, remolque].map((s) => s.trim()).filter(Boolean) : [],
        conductor: conductor.trim() || null,
        precio_transporte: precio ? Number(String(precio).replace(",", ".")) : null,
        autorizacion_especial: autorizacion.trim() || null,
        observaciones: observaciones.trim() || null,
        origen,
        modo,
        tipo_deca: tipoDeca === "proveedor" ? "proveedor" : "cliente",
        cargador_contractual: puedeElegirCargador ? cargadorContractual : "empresa",
        proveedor: tipoDeca === "proveedor" ? proveedor : undefined,
        borrador: !!comoBorrador,
        envios: envios.map((e) => ({
          origen: e.origen || origen,
          destinatario: e.destinatario,
          destino: e.destino || destinoDe(e.destinatario),
          destinatario: e.dest_id ? {
            ...e.destinatario,
            domicilio: e.destinatario?.domicilio || "",
            municipio: e.destinatario?.municipio || "",
            cp: e.destinatario?.cp || "",
            provincia: e.destinatario?.provincia || "",
          } : e.destinatario,
          naturaleza: e.naturaleza,
          bultos: e.bultos === "" ? null : Number(e.bultos),
          peso_bruto: Number(String(e.peso_bruto).replace(",", ".")),
          albaran_ejercicio: e.albaran_ejercicio ? Number(e.albaran_ejercicio) : null,
          albaran_serie: String(e.albaran_serie || "").trim(),
          albaran_numero: e.albaran_numero,
          fecha_albaran: fecha,
        })),
      };
      const { generados, recuperado } = await postGenerarConRecuperacion(
        "/api/v1/decas/generar-manual",
        payload,
        { transportistaNif: transportista?.nif, fecha, fuente: "manual" },
      );
      if (recuperado) {
        toast.info("El servidor tard" + String.fromCharCode(0xf3) + ", pero el DeCA s" + String.fromCharCode(0xed) + " se emiti" + String.fromCharCode(0xf3) + ".");
      } else if (comoBorrador) {
        toast.success("Borrador guardado. Completa el transportista y la matr" + String.fromCharCode(0xed) + "cula para emitirlo.");
      }
      setAskBorrador(false);
      liberar();
      irAGenerados(nav, generados, { abrirEmail: !comoBorrador && !generados.some((g) => g.estado === "borrador") });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <AvisoSalida blocker={blocker} saliendo={saliendo} confirmar={confirmar} cancelar={cancelar} />
      <ConfirmarBorrador
        open={askBorrador}
        onOpenChange={setAskBorrador}
        busy={busy}
        onConfirm={() => generar(true)}
      />
      <PageHeader
        title="DeCA manual"
        subtitle={tipoDeca === "proveedor" ? T.hintProveedor : T.subtitle}
        testid="page-deca-manual"
        actions={<>
          <Button variant="outline" onClick={() => nav("/decas")}><ChevronLeft className="h-4 w-4 mr-2"/> {T.historico}</Button>
          <Button variant="outline" onClick={() => generar(true)} disabled={busy} data-testid="btn-borrador-manual">
            Guardar borrador
          </Button>
          <Button onClick={() => generar(false)} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-generar-manual">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <FilePlus2 className="h-4 w-4 mr-2"/>}
            {busy ? "Generando" + String.fromCharCode(0x2026) : (nDecas > 1 ? T.seGeneraran(nDecas) : "Generar DeCA")}
          </Button>
        </>}
      />

      {puedeProveedor ? (
        <Card className="p-3 mb-4">
          <div className="label-tag mb-1">{"Tipo de DeCA"}</div>
          <Tabs value={tipoDeca} onValueChange={(v) => aplicarTipo(v)}>
            <TabsList data-testid="manual-tipo-deca">
              <TabsTrigger value="cliente">{T.tipoCliente}</TabsTrigger>
              <TabsTrigger value="proveedor">{T.tipoProveedor}</TabsTrigger>
            </TabsList>
          </Tabs>
          {tipoDeca === "proveedor" ? <p className="text-xs text-slate-500 mt-2">{T.hintProveedor}</p> : null}
        </Card>
      ) : null}

      <div className="grid xl:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-blue-700"/>
            <div className="label-tag mb-0">{tipoDeca === "proveedor" ? T.destinoEmpresa : "Cargador contractual"}</div>
          </div>
          {puedeElegirCargador ? (
            <div className="mb-3" data-testid="manual-cargador-contractual">
              <Tabs value={cargadorContractual} onValueChange={setCargadorContractual}>
                <TabsList data-testid="manual-tipo-cargador">
                  <TabsTrigger value="empresa">Esta empresa</TabsTrigger>
                  <TabsTrigger value="cliente">El cliente</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-xs text-slate-500 mt-2">
                {cargadorContractual === "cliente"
                  ? "El cargador contractual del PDF ser" + String.fromCharCode(0xe1) + " el cliente. El origen de carga sigue siendo el de esta empresa."
                  : "El cargador contractual es el de esta empresa. El origen no cambia."}
              </p>
            </div>
          ) : null}
          <PartePicker
            label={tipoDeca === "proveedor" ? "Empresa destino" : "Cargador"}
            tipo="cargador"
            items={opciones?.cargadores || []}
            value={cargador}
            onChange={(p) => {
              setCargador(p);
              if (tipoDeca === "proveedor") {
                const dest = destinoDe(p);
                setEnvios((rows) => rows.map((r) => ({
                  ...r,
                  destinatario: p || emptyParte(),
                  destino: dest,
                  dest_id: dest ? "empresa" : "",
                })));
              } else {
                const ori = destinoDe(p) || (opciones?.origen_default || "");
                setOrigen(ori);
                setEnvios((rows) => rows.map((r) => ({
                  ...r,
                  origen: (!r.orig_id || r.orig_id === "cargador") ? (ori || r.origen) : r.origen,
                  orig_id: (!r.orig_id || r.orig_id === "cargador") ? "cargador" : r.orig_id,
                })));
              }
            }}
            onCreate={() => openCreate("cargador", "cargador")}
            placeholder={T.buscarCargador}
          />
          {tipoDeca === "proveedor" ? (
            <div className="mt-3">
              <PartePicker
                label="Proveedor origen"
                tipo="proveedor"
                items={opciones?.proveedores || []}
                value={proveedor}
                onChange={(p) => {
                  setProveedor(p);
                  const ori = destinoDe(p);
                  setOrigen(ori);
                  setEnvios((rows) => rows.map((r) => ({ ...r, origen: ori || r.origen })));
                }}
                onCreate={() => openCreate("proveedor", "proveedor")}
                placeholder={T.buscarProveedor}
              />
            </div>
          ) : null}
          <div className="mt-3">
            <Field label={tipoDeca === "proveedor" ? T.origenProveedor : "Origen (calle, CP, municipio y provincia)"}>
              <Input value={origen} maxLength={LEN.domicilio} onChange={(e) => setOrigen(e.target.value)} className="mt-1" placeholder={T.origenPh}/>
            </Field>
            <div className="text-[11px] text-slate-500 mt-1">{tipoDeca === "proveedor" ? T.hintProveedor : T.copiaAlbaran}</div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="h-4 w-4 text-blue-700"/>
            <div className="label-tag mb-0">Datos del transporte</div>
          </div>
          {!esConductor ? (
            <PartePicker
              label="Transportista efectivo"
              tipo="transportista"
              items={opciones?.transportistas || []}
              value={transportista}
              onChange={(p) => { setTransportista(p); setConductor(""); setVehiculo(""); setRemolque(""); }}
              onCreate={() => openCreate("transportista", "transportista")}
              placeholder={T.buscarTransp}
            />
          ) : (
            <div className="text-sm text-slate-600 border border-slate-200 rounded-md p-3 bg-slate-50">
              {T.sesionConductor}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Fecha del transporte">
              <DatePicker value={fecha} onChange={setFecha} testid="manual-fecha"/>
            </Field>
            <div>
              <Field label="Fecha de descarga">
                <DatePicker value={fechaDescarga} onChange={setFechaDescarga} testid="manual-fecha-descarga"/>
              </Field>
              <div className="text-[11px] text-slate-500 mt-1">
                Estimada de fin de transporte. Si est{String.fromCharCode(0xe1)} vac{String.fromCharCode(0xed)}a no sale en el PDF.
              </div>
            </div>
            <MatriculaSelect transportistaNif={transportista?.nif} transportistaRazon={transportista?.razon_social} transportistaCodigo={transportista?.codigo} tipo="vehiculo" required value={vehiculo} onChange={setVehiculo} testid="manual-vehiculo"/>
            <MatriculaSelect transportistaNif={transportista?.nif} transportistaRazon={transportista?.razon_social} transportistaCodigo={transportista?.codigo} tipo="remolque" value={remolque} onChange={setRemolque} testid="manual-remolque"/>
            <ConductorSelect transportistaNif={transportista?.nif} transportistaRazon={transportista?.razon_social} transportistaCodigo={transportista?.codigo} value={conductor} onChange={setConductor} testid="manual-conductor"/>
            <Field label="Precio del transporte">
              <Input className="mt-1" inputMode="decimal" maxLength={LEN.precio} value={precio} onChange={(e)=>setPrecio(e.target.value)} placeholder="105,10"/>
            </Field>
            <Field className="sm:col-span-2" label={T.autorizacion}>
              <Input className="mt-1" maxLength={LEN.autorizacion} value={autorizacion} onChange={(e)=>setAutorizacion(e.target.value)}/>
            </Field>
            <Field className="sm:col-span-2" label="Observaciones">
              <Textarea rows={2} className="mt-1" maxLength={LEN.observaciones} value={observaciones} onChange={(e)=>setObservaciones(e.target.value)}/>
            </Field>
          </div>
        </Card>
      </div>

      <Card className="p-4 mt-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-blue-700"/>
            <div>
              <div className="label-tag mb-0">{T.envios} ({envios.length})</div>
              <div className="text-xs text-slate-500">{T.mismosDatos}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={modo} onValueChange={setModo}>
              <TabsList data-testid="manual-mode-tabs">
                <TabsTrigger value="unico" data-testid="manual-mode-unico">Un solo DeCA</TabsTrigger>
                <TabsTrigger value="agrupado" data-testid="manual-mode-agrupado">Por destino</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button type="button" variant="outline" onClick={() => setEnvios((rows) => [...rows, emptyEnvio(origen, opciones?.naturaleza_por_defecto || user?.naturaleza_por_defecto, bultosDefectoDe(opciones) || bultosDefectoDe(user))])}>
              <Plus className="h-4 w-4 mr-2"/> {T.anadirAlbaran}
            </Button>
          </div>
        </div>
        {envios.length > 0 ? (
          <div className="mb-3 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-900" data-testid="manual-grupos-hint">
            <span className="font-semibold">{T.seGeneraran(nDecas)}.</span>{" "}
            {modo === "agrupado" ? T.reglaGrupos : T.reglaUnico}
            {modo === "agrupado" && gruposDestino.some((g) => g.n > 1) ? (
              <span>
                {" "}Agrupados: {gruposDestino.filter((g) => g.n > 1).map((g) => `${g.nombre} (${g.n})`).join(", ")}.
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-4">
          {envios.map((e, idx) => (
            <div key={e.key} className="border border-slate-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>{T.albaranN(idx + 1)}</div>
                {envios.length > 1 ? (
                  <Button type="button" variant="ghost" size="sm" className="text-red-700" onClick={() => setEnvios((rows) => rows.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-3.5 w-3.5 mr-1"/> Quitar
                  </Button>
                ) : null}
              </div>
              <div className="grid lg:grid-cols-2 gap-3">
                {tipoDeca === "proveedor" ? (
                  <div className="lg:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="label-tag">{T.destinoEmpresa}</div>
                    <div className="text-sm font-semibold text-slate-900">{e.destinatario?.razon_social || cargador?.razon_social || T.dash}</div>
                    <div className="text-xs text-slate-500">{e.destino || destinoDe(cargador) || T.dash}</div>
                  </div>
                ) : (
                  <>
                <PartePicker
                  label="Cliente destino"
                  tipo="cliente"
                  items={opciones?.clientes || []}
                  value={e.destinatario?.razon_social ? e.destinatario : null}
                  onChange={(p) => {
                    const texto = destinoDe(p);
                    setEnvio(idx, {
                      destinatario: p || emptyParte(),
                      cliente_dir: p ? {
                        domicilio: p.domicilio || "",
                        municipio: p.municipio || "",
                        cp: p.cp || "",
                        provincia: p.provincia || "",
                      } : null,
                      destino: texto,
                      dest_id: texto ? "cliente" : "",
                    });
                  }}
                  onCreate={() => openCreate("cliente", idx)}
                  placeholder={T.buscarCliente}
                />
                <DestinoClienteSelect
                  clienteNif={e.destinatario?.nif}
                  clienteDir={e.cliente_dir || e.destinatario}
                  valueId={e.dest_id || (destinoDe(e.destinatario) ? "cliente" : "none")}
                  testid={`manual-dest-${idx}`}
                  onSelect={(sel) => setEnvio(idx, {
                    dest_id: sel.id || "libre",
                    destino: sel.texto || "",
                    destinatario: {
                      ...(e.destinatario || emptyParte()),
                      domicilio: sel.id === "cliente" ? (e.cliente_dir?.domicilio || sel.domicilio || "") : (sel.domicilio || ""),
                      municipio: sel.id === "cliente" ? (e.cliente_dir?.municipio || sel.municipio || "") : (sel.municipio || ""),
                      cp: sel.id === "cliente" ? (e.cliente_dir?.cp || sel.cp || "") : (sel.cp || ""),
                      provincia: sel.id === "cliente" ? (e.cliente_dir?.provincia || sel.provincia || "") : (sel.provincia || ""),
                    },
                  })}
                />
                  </>
                )}
                <Field className="lg:col-span-2" label={tipoDeca === "proveedor" ? T.destinoEmpresa : T.direccion}>
                  <Input className="mt-1 h-9" maxLength={LEN.domicilio} value={e.destino} onChange={(ev)=>setEnvio(idx,{ destino: ev.target.value, dest_id: "libre" })}/>
                </Field>
                {tipoDeca === "proveedor" ? (
                  <Field className="lg:col-span-2" label="Origen (calle, CP, municipio y provincia)">
                    <Input className="mt-1" maxLength={LEN.domicilio} value={e.origen} onChange={(ev)=>setEnvio(idx,{ origen: ev.target.value })} placeholder={T.origenPh}/>
                  </Field>
                ) : (
                  <div className="lg:col-span-2">
                    <DestinoClienteSelect
                      modo="cargador"
                      clienteNif={cargador?.nif}
                      clienteDir={cargador}
                      valueId={e.orig_id || (destinoDe(cargador) ? "cargador" : "none")}
                      testid={`manual-orig-${idx}`}
                      onSelect={(sel) => setEnvio(idx, {
                        orig_id: sel.id || "libre",
                        origen: sel.texto || fmtDomicilio(sel) || "",
                      })}
                    />
                    <Input className="mt-2" maxLength={LEN.domicilio} value={e.origen} onChange={(ev)=>setEnvio(idx,{ origen: ev.target.value, orig_id: "libre" })} placeholder={T.origenPh}/>
                  </div>
                )}
                <Field className="lg:col-span-2" label={T.naturaleza}>
                  <Input className="mt-1" maxLength={LEN.naturaleza} value={e.naturaleza} onChange={(ev)=>setEnvio(idx,{ naturaleza: ev.target.value })}/>
                </Field>
                <Field label="Bultos">
                  <Input className="mt-1" inputMode="decimal" maxLength={8} value={e.bultos} onChange={(ev)=>setEnvio(idx,{ bultos: ev.target.value })}/>
                </Field>
                <Field label="Peso bruto (kg)">
                  <Input className="mt-1" inputMode="decimal" maxLength={LEN.precio} value={e.peso_bruto} onChange={(ev)=>setEnvio(idx,{ peso_bruto: ev.target.value })}/>
                </Field>
                <Field label="Ejercicio">
                  <Input className="mt-1" inputMode="numeric" maxLength={4} value={e.albaran_ejercicio} onChange={(ev)=>setEnvio(idx,{ albaran_ejercicio: ev.target.value })}/>
                </Field>
                <Field label="Serie">
                  <Input className="mt-1" maxLength={LEN.serie} value={e.albaran_serie} onChange={(ev)=>setEnvio(idx,{ albaran_serie: ev.target.value })} placeholder={T.vacio}/>
                </Field>
                <Field label={T.nAlbaran}>
                  <Input className="mt-1" inputMode="numeric" maxLength={12} value={e.albaran_numero} onChange={(ev)=>setEnvio(idx,{ albaran_numero: ev.target.value })}/>
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {createTipo === "cliente" ? "Nuevo cliente destino" : createTipo === "cargador" ? "Nuevo cargador" : createTipo === "proveedor" ? "Nuevo proveedor" : "Nuevo transportista"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field className="sm:col-span-2" label={T.razonSocial}>
              <Input className="mt-1" maxLength={LEN.razonSocial} value={createForm.razon_social} onChange={(e)=>setCreateForm({...createForm, razon_social:e.target.value})}/>
            </Field>
            <Field label={createTipo === "cliente" || createTipo === "proveedor" ? "NIF / VAT *" : "NIF *"}>
              <Input className="mt-1 font-mono uppercase" maxLength={LEN.nif} value={createForm.nif} onChange={(e)=>setCreateForm({...createForm, nif:e.target.value})}/>
            </Field>
            <Field label={T.codigoPostal}>
              <Input className="mt-1" inputMode="numeric" maxLength={LEN.cp} value={createForm.cp} onChange={(e)=>setCreateForm({...createForm, cp:e.target.value})}/>
            </Field>
            <Field className="sm:col-span-2" label="Domicilio">
              <Input className="mt-1" maxLength={LEN.domicilio} value={createForm.domicilio} onChange={(e)=>setCreateForm({...createForm, domicilio:e.target.value})}/>
            </Field>
            <Field label="Municipio">
              <Input className="mt-1" maxLength={LEN.municipio} value={createForm.municipio} onChange={(e)=>setCreateForm({...createForm, municipio:e.target.value})}/>
            </Field>
            <Field label="Provincia">
              <Input className="mt-1" maxLength={LEN.provincia} value={createForm.provincia} onChange={(e)=>setCreateForm({...createForm, provincia:e.target.value})}/>
            </Field>
            {createTipo === "transportista" ? (
              <Field className="sm:col-span-2" label={T.correoEnvio}>
                <Input className="mt-1" type="email" maxLength={LEN.email} value={createForm.email} onChange={(e)=>setCreateForm({...createForm, email:e.target.value})} placeholder="trafico@agencia.com"/>
              </Field>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={saveParte} className="bg-blue-700 hover:bg-blue-800">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
